(() => {
  if (customElements.get('st-product-flow')) return;
  class StampadikoProductFlow extends HTMLElement {
    connectedCallback() {
      // Wait for child form and inline variant data, including in theme previews.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else {
        queueMicrotask(() => this.initialize());
      }
    }
    initialize() {
      if (this.ready || !this.isConnected) return;
      this.form = this.querySelector('[data-st-form], form.stpf__form');
      this.region = this.querySelector('[data-st-variant-region]');
      if (!this.form || !this.region) return;
      this.ready = true;
      this.button = this.querySelector('[data-st-add]');
      this.status = this.querySelector('[data-st-status]');
      this.error = this.querySelector('[data-st-error]');
      this.retry = this.querySelector('[data-st-retry]');
      this.fileInput = this.querySelector('[data-st-artwork]');
      this.notes = this.querySelector('[data-st-notes]');
      this.preview = this.querySelector('[data-st-personalizer]');
      this.previewArtwork = this.querySelector('[data-st-preview-artwork]');
      this.previewEmpty = this.querySelector('[data-st-preview-empty]');
      this.placement = this.querySelector('[data-st-placement]');
      this.printSize = this.querySelector('[data-st-print-size]');
      this.scale = this.querySelector('[data-st-scale]');
      this.offsetX = this.querySelector('[data-st-x]');
      this.offsetY = this.querySelector('[data-st-y]');
      this.propScale = this.querySelector('[data-st-prop-scale]');
      this.propX = this.querySelector('[data-st-prop-x]');
      this.propY = this.querySelector('[data-st-prop-y]');
      this.pending = false;
      this.failed = false;
      this.submitting = false;
      this.sequence = 0;
      this.inlineVariants = this.readInlineVariants();
      this.enableOptions();
      if (this.inlineVariants) this.applyInlineSelection(false);
      else this.refreshButton();
      this.syncOptionCards();
      this.addEventListener('change', event => {
        if (event.target.matches('[data-st-option]')) this.changeVariant(event.target);
        if (event.target === this.fileInput) {
          this.updateFileMessage();
          this.updateArtworkPreview();
        }
        if (event.target === this.placement || event.target === this.printSize) this.updatePersonalizer();
        if (event.target === this.fileInput || event.target === this.notes) this.clearPersonalizationError();
      });
      this.notes?.addEventListener('input', () => this.clearPersonalizationError());
      [this.scale, this.offsetX, this.offsetY].forEach(input => input?.addEventListener('input', () => this.updatePersonalizer()));
      this.updatePersonalizer();
      this.addEventListener('click', event => {
        const optionCard = event.target.closest('[data-st-option-card]');
        if (optionCard && this.contains(optionCard)) {
          event.preventDefault();
          this.chooseOptionCard(optionCard);
          return;
        }
        const thumb = event.target.closest('[data-st-thumb]');
        if (thumb && this.contains(thumb)) { event.preventDefault(); this.showImage(thumb); }
        if (event.target.closest('[data-st-retry]')) { event.preventDefault(); this.changeVariant(); }
      });
      this.form.addEventListener('submit', event => this.beforeSubmit(event));
      window.addEventListener('pageshow', () => {
        if (!this.isConnected) return;
        this.submitting = false;
        if (this.inlineVariants) this.applyInlineSelection(false);
        else { this.refreshButton(); if (this.failed) this.showFailure(); }
      });
    }
    readInlineVariants() {
      try {
        const node = this.querySelector('[data-st-inline-variants]');
        if (!node) return null;
        const data = JSON.parse(node.textContent);
        const optionCount = this.querySelectorAll('[data-st-option]').length;
        if (String(data.productId) !== this.dataset.productId || !Array.isArray(data.variants) ||
            data.count !== data.variants.length || data.count < 1 || data.count > 100 || !optionCount) return null;
        const variants = new Map();
        for (const variant of data.variants) {
          if (!/^\d+$/.test(String(variant.id)) || !Array.isArray(variant.options) ||
              variant.options.length !== optionCount || !variant.options.every(value => typeof value === 'string') ||
              typeof variant.priceText !== 'string' || typeof variant.canBuy !== 'boolean') return null;
          const key = JSON.stringify(variant.options);
          if (variants.has(key)) return null;
          variants.set(key, variant);
        }
        return variants;
      } catch (_) { return null; }
    }
    selectedNames() {
      return Array.from(this.querySelectorAll('[data-st-option]'), select => select.selectedOptions[0]?.dataset.stOptionName ?? null);
    }
    applyInlineSelection(updateUrl = true) {
      if (!this.inlineVariants) return false;
      const input = this.region.querySelector('input[name="id"]');
      const price = this.region.querySelector('[data-st-price]');
      if (!input || !price) return false;
      const variant = this.inlineVariants.get(JSON.stringify(this.selectedNames()));
      this.controller?.abort();
      ++this.sequence;
      this.pending = false;
      this.failed = false;
      this.region.removeAttribute('aria-busy');
      this.region.dataset.variantId = variant ? String(variant.id) : '';
      this.region.dataset.canBuy = String(!!variant?.canBuy);
      this.region.dataset.unavailableText = variant?.unavailableText || this.message('unavailable');
      input.value = variant ? String(variant.id) : '';
      input.disabled = !variant?.canBuy;
      // One variant controls both the displayed price and the cart form's ID.
      price.textContent = variant ? variant.priceText : this.message('unavailable');
      if (this.retry) this.retry.hidden = true;
      this.setStatus(variant ? '' : this.message('unavailable'));
      if (variant?.imageId) {
        const thumb = Array.from(this.querySelectorAll('[data-st-thumb]')).find(el => el.dataset.stImageId === String(variant.imageId));
        if (thumb) this.showImage(thumb);
      }
      this.refreshButton();
      this.syncOptionCards();
      if (updateUrl) this.updateUrl(this.region.dataset.variantId);
      return true;
    }
    updateUrl(variantId, optionValues) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      if (variantId) {
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
        const values = optionValues || Array.from(this.querySelectorAll('[data-st-option]'), select => select.value);
        url.searchParams.set('option_values', values.join(','));
      }
      try { history.replaceState(history.state, '', url.href); } catch (_) { /* Embedded previews can restrict history updates. */ }
    }
    enableOptions() { this.querySelectorAll('[data-st-options]').forEach(el => { el.hidden = false; }); }
    message(name) { return this.dataset[name] || ''; }
    setStatus(text) { if (this.status) this.status.textContent = text; }
    refreshButton() {
      if (!this.button || !this.region) return;
      const purchasable = this.region.dataset.canBuy === 'true' && !!this.region.dataset.variantId;
      this.button.disabled = this.pending || this.failed || this.submitting || !purchasable;
      this.button.textContent = this.pending ? this.message('loading') : (this.submitting ? this.message('adding') : (purchasable ? this.message('add') : (this.region.dataset.unavailableText || this.message('unavailable'))));
      const contact = this.querySelector('[data-st-contact]');
      if (contact) contact.hidden = purchasable || this.pending || this.failed;
    }
    async changeVariant(changed) {
      if (this.submitting || this.dataset.singleVariant === 'true') return;
      const values = Array.from(this.querySelectorAll('[data-st-option]'), select => select.value);
      const selected = changed?.selectedOptions?.[0];
      if (selected?.dataset.productUrl && new URL(selected.dataset.productUrl, window.location.origin).pathname !== new URL(this.dataset.productUrl, window.location.origin).pathname) {
        this.failed = true;
        this.refreshButton();
        this.showFailure();
        return;
      }
      if (this.applyInlineSelection()) return;
      // High-variant products keep Shopify's server-rendered option resolution.
      const requestedNames = this.selectedNames();
      const url = new URL(this.dataset.productUrl, window.location.origin);
      const current = new URL(window.location.href);
      url.search = current.search;
      url.searchParams.delete('variant');
      url.searchParams.delete('section_id');
      url.searchParams.set('option_values', values.join(','));
      url.searchParams.set('sections', this.dataset.sectionId);
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const serial = ++this.sequence;
      this.pending = true;
      this.failed = false;
      if (this.retry) this.retry.hidden = true;
      this.region.setAttribute('aria-busy', 'true');
      this.setStatus(this.message('loading'));
      this.refreshButton();
      const timer = window.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal, headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const sections = await response.json();
        const html = sections[this.dataset.sectionId];
        if (!html || typeof html !== 'string') throw new Error('Missing rendered section');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = Array.from(doc.querySelectorAll('st-product-flow')).find(el => el.dataset.productId === this.dataset.productId);
        const nextRegion = fresh?.querySelector('[data-st-variant-region]');
        const nextGallery = fresh?.querySelector('[data-st-gallery]');
        if (!nextRegion || !nextGallery) throw new Error('Incomplete product response');
        const receivedNames = Array.from(nextRegion.querySelectorAll('[data-st-option]'), select => select.selectedOptions[0]?.dataset.stOptionName ?? null);
        if (nextRegion.dataset.variantId && JSON.stringify(receivedNames) !== JSON.stringify(requestedNames)) throw new Error('Selection mismatch');
        if (serial !== this.sequence || !this.isConnected) return;
        this.region.replaceWith(nextRegion);
        this.region = nextRegion;
        this.querySelector('[data-st-gallery]')?.replaceWith(nextGallery);
        this.syncPreviewProductFromGallery();
        this.enableOptions();
        this.syncOptionCards();
        this.pending = false;
        this.failed = false;
        this.region.removeAttribute('aria-busy');
        this.setStatus(nextRegion.dataset.variantId ? '' : this.message('unavailable'));
        this.refreshButton();
        this.updateUrl(nextRegion.dataset.variantId, values);
        if (changed?.id) this.querySelectorAll('[data-st-option]').forEach(el => { if (el.id === changed.id) el.focus({ preventScroll: true }); });
      } catch (error) {
        if (serial !== this.sequence || !this.isConnected) return;
        this.pending = false;
        this.failed = true;
        this.region.removeAttribute('aria-busy');
        this.showFailure();
        this.refreshButton();
      } finally { window.clearTimeout(timer); }
    }
    showFailure() { this.setStatus(this.message('failure')); if (this.retry) this.retry.hidden = false; }
    showImage(thumb) {
      const main = this.querySelector('[data-st-gallery-main] img');
      const link = this.querySelector('[data-st-gallery-main]');
      if (!main) return;
      main.src = thumb.dataset.stLarge;
      main.srcset = thumb.dataset.stSrcset || '';
      main.alt = thumb.dataset.stAlt || '';
      if (link) link.href = thumb.dataset.stOriginal || thumb.dataset.stLarge;
      this.querySelectorAll('[data-st-thumb]').forEach(el => el.setAttribute('aria-pressed', String(el === thumb)));
      this.syncPreviewProductFromGallery();
    }
    syncPreviewProductFromGallery() {
      const source = this.querySelector('[data-st-gallery-main] img');
      const target = this.querySelector('[data-st-preview-product]');
      if (!source || !target) return;
      target.src = source.currentSrc || source.src;
      target.srcset = source.srcset || '';
      target.alt = source.alt || '';
    }
    updateArtworkPreview() {
      if (!this.previewArtwork || !this.previewEmpty) return;
      if (this.previewObjectUrl) {
        URL.revokeObjectURL(this.previewObjectUrl);
        this.previewObjectUrl = null;
      }
      const file = this.fileInput?.files?.[0];
      if (!file) {
        this.previewArtwork.hidden = true;
        this.previewArtwork.removeAttribute('src');
        this.previewEmpty.hidden = false;
        this.updatePersonalizer();
        return;
      }
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!['png','jpg','jpeg','svg','webp'].includes(extension)) {
        this.previewArtwork.hidden = true;
        this.previewArtwork.removeAttribute('src');
        this.previewEmpty.hidden = false;
        this.previewEmpty.textContent = file.name;
        this.updatePersonalizer();
        return;
      }
      this.previewObjectUrl = URL.createObjectURL(file);
      this.previewArtwork.src = this.previewObjectUrl;
      this.previewArtwork.hidden = false;
      this.previewEmpty.hidden = true;
      this.updatePersonalizer();
    }
    updatePersonalizer() {
      if (!this.preview) return;
      const zone = this.querySelector('[data-st-preview-zone]');
      if (!zone) return;
      const placement = this.placement?.value || 'Main print area';
      const sleeve = placement.includes('sleeve');
      if (this.printSize) {
        if (sleeve) {
          this.printSize.value = 'A6';
          this.printSize.disabled = true;
        } else {
          this.printSize.disabled = false;
        }
      }
      const size = sleeve ? 'A6' : (this.printSize?.value || 'A4');
      const sizeScale = { A6: 0.58, A5: 0.76, A4: 1, A3: 1.22 }[size] || 1;
      const userScale = Number(this.scale?.value || 100) / 100;
      const x = Number(this.offsetX?.value || 0);
      const y = Number(this.offsetY?.value || 0);
      zone.dataset.placement = placement;
      zone.dataset.size = size;
      zone.style.setProperty('--stp-scale', String(sizeScale * userScale));
      zone.style.setProperty('--stp-x', x + '%');
      zone.style.setProperty('--stp-y', y + '%');
      if (this.propScale) this.propScale.value = Math.round(userScale * 100) + '%';
      if (this.propX) this.propX.value = String(x);
      if (this.propY) this.propY.value = String(y);
    }
    chooseOptionCard(card) {
      const position = card.dataset.optionPosition;
      const value = card.dataset.optionValue;
      const select = this.querySelector('[data-st-option][data-option-position="' + position + '"]');
      if (!select || !value || select.value === value) {
        this.syncOptionCards();
        return;
      }
      select.value = value;
      this.syncOptionCards();
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncOptionCards() {
      this.querySelectorAll('[data-st-option-card]').forEach(card => {
        const select = this.querySelector('[data-st-option][data-option-position="' + card.dataset.optionPosition + '"]');
        card.setAttribute('aria-pressed', String(!!select && select.value === card.dataset.optionValue));
      });
    }
    updateFileMessage() {
      const target = this.querySelector('[data-st-file-name]');
      if (target) target.textContent = this.fileInput?.files?.[0]?.name || '';
    }
    clearPersonalizationError() {
      this.notes?.setCustomValidity('');
      this.fileInput?.setCustomValidity('');
      if (this.error) { this.error.hidden = true; this.error.textContent = ''; }
    }
    beforeSubmit(event) {
      this.clearPersonalizationError();
      if (this.inlineVariants && !this.submitting && !this.pending && !this.failed) this.applyInlineSelection(false);
      if (this.pending || this.failed || this.submitting || this.region.dataset.canBuy !== 'true') {
        event.preventDefault();
        this.refreshButton();
        if (this.failed) this.showFailure();
        return;
      }
      const variantInput = this.region.querySelector('input[name="id"]');
      if (!variantInput?.value) { event.preventDefault(); return; }
      const file = this.fileInput?.files?.[0];
      if (this.dataset.requirePersonalization === 'true' && !file && !this.notes?.value.trim()) {
        event.preventDefault();
        if (this.error) { this.error.hidden = false; this.error.textContent = this.message('personalizationError'); }
        if (this.notes) { this.notes.setCustomValidity(this.message('personalizationError')); this.notes.reportValidity(); this.notes.focus(); }
        return;
      }
      const extension = file?.name.split('.').pop()?.toLowerCase();
      if (file && !['pdf', 'png', 'jpg', 'jpeg', 'svg', 'ai', 'eps'].includes(extension)) {
        event.preventDefault();
        if (this.error) { this.error.hidden = false; this.error.textContent = this.message('fileError'); }
        this.fileInput.setCustomValidity(this.message('fileError'));
        this.fileInput.reportValidity();
        return;
      }
      if (!this.form.checkValidity()) { event.preventDefault(); this.form.reportValidity(); return; }
      this.submitting = true;
      this.refreshButton();
    }
  }
  customElements.define('st-product-flow', StampadikoProductFlow);
})();
